import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ReCaptchaService } from './re-captcha.service';

@Component({
  selector: 're-captcha',
  templateUrl: './re-captcha.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ReCaptchaComponent),
      multi: true,
    },
  ],
  standalone: true,
})
export class ReCaptchaComponent implements OnInit, OnDestroy, ControlValueAccessor {
  /* Available languages: https://developers.google.com/recaptcha/docs/language */
  @Input() public language: string = null;
  @Input() public tabindex = 0;
  @Input() public global = false;
  @Input() public site_key: string = null;
  @Input() public theme = 'light';
  @Input() public type = 'image';
  @Input() public size = 'normal';
  @Input() public badge = 'bottomright';

  @Output() public loaded = new EventEmitter<boolean>();
  @Output() public captchaExpired = new EventEmitter();
  @Output() public captchaResponse = new EventEmitter<string>();

  @ViewChild('target') public targetRef: ElementRef;
  public widgetId: any = null;

  public unsubscribe$ = new Subject<void>();

  public onChange: (value: any) => void = () => undefined;
  public onTouched: () => void = () => undefined;

  private resizeObserver: ResizeObserver;

  constructor(private zone: NgZone, private captchaService: ReCaptchaService) {}

  public ngOnInit() {
    this.resizeObserver = new ResizeObserver(() => {
      if (this.widgetId !== null && this.widgetId !== undefined) {
        this.rePositionCaptchaPopup();
      }
    });

    this.resizeObserver.observe(document.body);

    this.captchaService
      .getReady(this.language, this.global)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((ready: boolean) => {
        if (!ready) return;
        // noinspection TypeScriptUnresolvedVariable,TypeScriptUnresolvedFunction
        setTimeout(() => {
          if (this.widgetId === null || this.widgetId === undefined) {
            this.widgetId = (<any>window).grecaptcha.render(this.targetRef.nativeElement, {
              'sitekey': this.site_key,
              'badge': this.badge,
              'theme': this.theme,
              'type': this.type,
              'size': this.size,
              'tabindex': this.tabindex,
              'callback': <any>((response: any) => this.zone.run(this.recaptchaCallback.bind(this, response))),
              'expired-callback': <any>(() => this.zone.run(this.recaptchaExpiredCallback.bind(this))),
            });
          }
          this.loaded.emit(true);
        }, 0);
      });
  }

  public ngOnDestroy() {
    this.resizeObserver.disconnect();

    this.unsubscribe$.next();
    this.unsubscribe$.complete();

    // this.reset();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public setDisabledState?(_isDisabled: boolean) {
    throw new Error('Method not implemented.');
  }

  // noinspection JSUnusedGlobalSymbols
  public reset() {
    if (this.widgetId === null || this.widgetId === undefined) return;
    // noinspection TypeScriptUnresolvedVariable
    this.zone.runOutsideAngular((<any>window).grecaptcha.reset.bind(this.widgetId));
    this.onChange(null);
  }

  // noinspection JSUnusedGlobalSymbols
  public execute() {
    if (this.widgetId === null || this.widgetId === undefined) return;
    // noinspection TypeScriptUnresolvedVariable
    (<any>window).grecaptcha.execute(this.widgetId);
    this.rePositionCaptchaPopup();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public writeValue(_value: any) {
    /* ignore it */
  }

  public registerOnChange(fn: (value: any) => void) {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  private recaptchaCallback(response: string) {
    this.onChange(response);
    this.onTouched();
    this.captchaResponse.emit(response);
  }

  private recaptchaExpiredCallback() {
    this.onChange(null);
    this.onTouched();
    this.captchaExpired.emit();
  }

  // Captcha popup cannot be styled according to official docs
  // Feature request: https://github.com/google/recaptcha/issues/520
  private rePositionCaptchaPopup(): void {
    const body = document.body;
    const lastElement = body.lastElementChild;

    if (
      this.widgetId !== null &&
      this.widgetId !== undefined &&
      lastElement.tagName === 'DIV' &&
      lastElement.childElementCount === 2 &&
      lastElement.lastElementChild.tagName === 'DIV' &&
      lastElement.lastElementChild.firstElementChild.tagName === 'IFRAME'
    ) {
      const overlayElement = lastElement.firstElementChild as HTMLDivElement;
      const containerElement = lastElement.lastElementChild as HTMLDivElement;
      const iframeElement = lastElement.lastElementChild.firstElementChild as HTMLIFrameElement;
      const popupWidth = iframeElement.clientWidth;
      const popupHeight = iframeElement.clientHeight;
      const minPopupWidth = 300;
      const minPopupHeight = 480;

      if (containerElement.style.transform) {
        containerElement.style.transform = null;
        containerElement.style.transformOrigin = null;
      }

      if (popupWidth < minPopupWidth || popupHeight < minPopupHeight) {
        // Sometimes the popup doesn't have it's final size and it is still not visible when this point is reached
        // When the popup is hidden, it is smaller than the minimum size of the fully rendered element
        setTimeout(() => {
          this.rePositionCaptchaPopup();
        }, 100);
      } else {
        containerElement.style.position = 'fixed';
        containerElement.style.marginTop =
          overlayElement.clientHeight >= popupHeight ? `${(overlayElement.clientHeight - popupHeight) / 2}px` : '0px';

        if (overlayElement.clientHeight < popupHeight || overlayElement.clientWidth < popupWidth) {
          const scale =
            overlayElement.clientHeight < popupHeight
              ? overlayElement.clientHeight / popupHeight
              : overlayElement.clientWidth / popupWidth;
          containerElement.style.transform = `scale(${scale})`;
          containerElement.style.transformOrigin = '0 0';
        }
      }
    }
  }
}
